import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbDropdownModule, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { RouterLink } from '@angular/router';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

export type AutomationJobStatus = 'completed' | 'in_progress' | 'failed' | 'cancelled';

export interface AutomationJobRow {
  id?: string;
  jobId: string;
  createdAt: string | Date;
  completedAt?: string | Date;
  createdBy: string;
  createdByName?: string;
  status: AutomationJobStatus;
  inProgress?: boolean;
  totalItems?: number;
  successCount?: number;
  failedCount?: number;
  singleProjects: number;
  myaCount: number;
  expiringCount: number;
}

@Component({
  selector: 'bulk-batch-cloning-table',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPaginationModule, NgbDropdownModule, RouterLink, FaIconComponent],
  templateUrl: './bulk-batch-cloning-table.component.html',
  styleUrls: ['./bulk-batch-cloning-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkBatchCloningTableComponent {
  readonly title = input('Automation');
  readonly tabLabel = input('Batch Cloning');
  readonly rows = input<AutomationJobRow[]>([]);
  readonly totalItems = input(0);

  readonly fromDate = input('');
  readonly toDate = input('');

  @Output() fromDateChange = new EventEmitter<string>();
  @Output() toDateChange = new EventEmitter<string>();

  @Output() applyFilters = new EventEmitter<void>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() createJob = new EventEmitter<void>();
  @Output() viewDetails = new EventEmitter<AutomationJobRow>();

  readonly searchValue = signal('');
  readonly page = signal(1);
  readonly limit = signal(20);
  readonly isMobile = signal(false);

  faPlus = faPlus

  ngOnInit(): void {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewportBound, { passive: true });
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.updateViewportBound);
  }

  private updateViewportBound = () => this.updateViewport();

  private updateViewport(): void {
    this.isMobile.set(window.innerWidth < 768);
  }

  readonly filteredRows = computed(() => {
    const q = this.searchValue().trim().toLowerCase();
    const rows = this.rows() ?? [];

    if (!q) return rows;

    return rows.filter((row) =>
      [
        row.jobId,
        row.createdBy,
        row.createdByName,
        this.getStatusLabel(row.status),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  });

  readonly totalCount = computed(() => this.filteredRows().length);

  readonly pagedRows = computed(() => {
    const start = (this.page() - 1) * this.limit();
    const end = start + this.limit();
    return this.filteredRows().slice(start, end);
  });

  readonly totalJobs = computed(() => this.filteredRows().length);

  readonly completedCount = computed(
    () => this.filteredRows().filter((x) => x.status === 'completed').length
  );

  readonly inProgressCount = computed(
    () => this.filteredRows().filter((x) => x.status === 'in_progress').length
  );

  readonly cancelledCount = computed(
    () => this.filteredRows().filter((x) => x.status === 'cancelled').length
  );

  readonly failedCount = computed(
    () => this.filteredRows().filter((x) => x.status === 'failed').length
  );

  search(value: string): void {
    this.searchValue.set(value ?? '');
    this.page.set(1);
  }

  setCurrentPage(page: number): void {
    this.page.set(page);
  }

  setLimit(limit: number): void {
    this.limit.set(limit);
    this.page.set(1);
  }

  onFromDateChange(value: string): void {
    this.fromDateChange.emit(value);
  }

  onToDateChange(value: string): void {
    this.toDateChange.emit(value);
  }

  onApply(): void {
    this.page.set(1);
    this.applyFilters.emit();
  }

  onClear(): void {
    this.searchValue.set('');
    this.page.set(1);
    this.clearFilters.emit();
  }

  onViewDetails(row: AutomationJobRow): void {
    this.viewDetails.emit(row);
  }

  getStatusLabel(status: AutomationJobStatus): string {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'failed':
        return 'Failed';
      default:
        return '';
    }
  }

  formatDate(value: string | Date): string {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  trackByJobId(_: number, row: AutomationJobRow): string {
    return row.jobId;
  }
}